import { VideoPlayIcon } from '@fingertip/icons'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

import { getCloudflareStreamId } from './utils'

type Props = {
  websiteUrl?: string
  isModal?: boolean
  className?: string
}

export const CloudflareStreamBlock = ({
  websiteUrl,
  isModal,
  className,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const embedId = useMemo(() => getCloudflareStreamId(websiteUrl), [websiteUrl])

  // Extract customer code from the URL for iframe src
  const customerCode = useMemo(() => {
    if (!websiteUrl) return null
    const match = websiteUrl.match(
      /customer-([a-zA-Z0-9]+)\.cloudflarestream\.com/,
    )
    return match && match[1] ? match[1] : null
  }, [websiteUrl])

  if (!embedId || !customerCode) {
    return null
  }

  const iframeSrc = `https://customer-${customerCode}.cloudflarestream.com/${embedId}/iframe`
  const thumbnailSrc = `https://customer-${customerCode}.cloudflarestream.com/${embedId}/thumbnails/thumbnail.jpg?time=&height=600`
  const posterParam = encodeURIComponent(thumbnailSrc)

  if (!isModal) {
    return (
      <div
        className={cn(
          'relative flex size-full items-center justify-center overflow-hidden bg-black',
          className,
        )}
      >
        <iframe
          className="aspect-video w-full"
          src={`${iframeSrc}?poster=${posterParam}`}
          loading="lazy"
          width="100%"
          height="100%"
          title={t('cloudflare_stream_video_player')}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <Dialog
      modal={true}
      open={open}
      onOpenChange={(open) => {
        setOpen(open)
      }}
    >
      <DialogTrigger className="flex size-full outline-hidden!">
        <div
          className={cn(
            'flex size-full items-center justify-center overflow-hidden rounded-page-widget-inner',
            className,
          )}
          style={{
            backgroundImage: `url(${thumbnailSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="rounded-full bg-white/50 backdrop-blur">
            <VideoPlayIcon className="size-fluid-16" />
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="fixed h-[calc(100vh-100px)] max-h-screen w-full min-w-full items-center justify-center rounded-none! bg-black pb-fluid-8 pt-fluid-14">
        <VisuallyHidden>
          <DialogTitle>Cloudflare Stream</DialogTitle>
        </VisuallyHidden>

        <iframe
          className="aspect-video w-full"
          src={`${iframeSrc}?autoplay=true&poster=${posterParam}`}
          loading="lazy"
          width="100%"
          height="100%"
          title={t('cloudflare_stream_video_player')}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
        />
      </DialogContent>
    </Dialog>
  )
}
